import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, Kanban, Users, MapPin, Handshake,
  LogOut, Globe, Plus, Menu, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand, SiteFooter } from "@/components/Brand";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const navItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/listings", label: "Listing", icon: Building2, testid: "nav-listings" },
  { to: "/app/kanban", label: "Kanban", icon: Kanban, testid: "nav-kanban" },
  { to: "/app/leads", label: "Leads CRM", icon: Users, testid: "nav-leads" },
  { to: "/app/map", label: "Radar Peta", icon: MapPin, testid: "nav-map" },
  { to: "/app/cobroke", label: "Co-Broke", icon: Handshake, testid: "nav-cobroke" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();

  const initials = (user?.name || "A")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#0A0D14] text-slate-100">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/5 bg-[#0D1322] px-4 py-6 sticky top-0 h-screen">
        <Link to="/app/dashboard" className="flex justify-center mb-8" data-testid="sidebar-logo">
          <Brand className="h-[120px]" />
        </Link>

        <nav className="flex-1 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-orange-500/10 text-orange-300 border border-orange-500/20"
                    : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <Link to="/app/listings/new" data-testid="sidebar-new-listing-btn">
          <Button className="w-full bg-gradient-to-r from-[#FF5722] to-[#F97316] hover:from-[#F97316] hover:to-[#EA580C] font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Tambah Listing
          </Button>
        </Link>
      </aside>

      {/* Topbar mobile */}
      <header className="lg:hidden sticky top-0 z-50 flex items-center justify-between px-4 py-3 bg-[#0A0D14]/90 backdrop-blur-xl border-b border-white/5">
        <Link to="/app/dashboard" className="flex items-center gap-2" data-testid="topbar-logo">
          <Brand className="h-16" />
        </Link>
        <button
          onClick={() => setMobileOpen((x) => !x)}
          className="p-2 rounded-lg hover:bg-white/5"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 top-[57px] z-40 bg-[#0A0D14] p-4" data-testid="mobile-menu">
          <nav className="space-y-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium ${
                    isActive ? "bg-orange-500/10 text-orange-300" : "text-slate-300"
                  }`
                }
              >
                <n.icon className="w-5 h-5" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 border-t border-white/5 pt-4">
            <Link to="/app/listings/new" onClick={() => setMobileOpen(false)}>
              <Button className="w-full bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-1" /> Tambah Listing
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-white/5 bg-[#0A0D14]/60 backdrop-blur-xl sticky top-0 z-30">
          <div>
            <span className="text-[11px] uppercase tracking-widest text-orange-400 font-semibold">
              Agent Workspace
            </span>
            <h2 className="text-lg font-semibold text-slate-100">
              Halo, {user?.name?.split(" ")[0] || "Agent"}
            </h2>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-full border border-white/5 hover:border-orange-500/30 bg-[#141C2E]" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-orange-500/20 text-orange-300 text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
                <div className="text-left leading-tight">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-[11px] text-slate-500 capitalize">{user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#141C2E] border-white/10 text-slate-100">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={() => navigate(`/catalog/${user?.id}`)} data-testid="menu-public-catalog">
                <Globe className="w-4 h-4 mr-2" /> Katalog Publik Saya
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout} data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">{children}<SiteFooter className="mt-10" /></main>
      </div>
    </div>
  );
}
